import type {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import axios from 'axios';

import { Configuration } from '../enums/Configuration';
import { store } from '../state-management/store';
import {
    addInterceptors,
    urlFiller,
} from './errorUtils';
import type { CustomAxiosInstance } from './types';

let token = '';
let axiosInstance: AxiosInstance;

const headers = { Authorization: '' };

// Retry configuration
interface RetryConfig {
    maxRetries: number;
    baseDelay: number;
    maxDelay: number;
    retryCondition: (error: AxiosError) => boolean;
}

const defaultRetryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    retryCondition: (error: AxiosError) => error.response?.status ? error.response.status >= 500 : false,
};

// Retry utility function with exponential backoff
const retryRequest = async <T>(
    requestFn: () => Promise<T>,
    retryConfig: RetryConfig = defaultRetryConfig,
): Promise<T> => {
    let lastError: AxiosError;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error as AxiosError;

            // Don't retry if it's the last attempt or if retry condition is not met
            if (attempt === retryConfig.maxRetries || !retryConfig.retryCondition(lastError)) {
                throw lastError;
            }

            // Calculate delay with exponential backoff
            const delay = Math.min(
                retryConfig.baseDelay * Math.pow(2, attempt),
                retryConfig.maxDelay,
            );

            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError!;
};

const waitForInit = new Promise((resolve) => {
    const unsubscribe = store.subscribe(() => {
        const {
            auth, config,
        } = store.getState();
        if (config.state === Configuration.APPREADY && !!auth.authToken) {
            updateInstanceWithParameters(auth.authToken);
            resolve(true);
            unsubscribe();
        }
    });
});

store.subscribe(() => {
    const {
        auth, config,
    } = store.getState();
    if (config.state === Configuration.APPREADY && auth.authToken !== token) {
        updateInstanceWithParameters(auth.authToken);
    }
});

const http: CustomAxiosInstance = {
    get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.get(urlFiller(url), config)),
        );
    },
    delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.delete(urlFiller(url), config)),
        );
    },
    head<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.head(urlFiller(url), config)),
        );
    },
    options<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.options(urlFiller(url), config)),
        );
    },
    post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.post(urlFiller(url), data, config)),
        );
    },
    put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.put(urlFiller(url), data, config)),
        );
    },
    patch<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return waitForInit.then(() =>
            retryRequest(() => axiosInstance.patch(urlFiller(url), data, config)),
        );
    },
    CancelToken: axios.CancelToken,
};

/* eslint-enable */

const updateInstanceWithParameters = (authToken: string): void => {
    token = authToken;

    headers.Authorization = `Bearer ${authToken}`;

    axiosInstance = axios.create({
    /* Increase the timeout to 20 seconds, Pipeline kill is taking more than 10 seconds */
        timeout: 0,
        headers: { Authorization: `Bearer ${authToken}` },
    });

    addInterceptors(axiosInstance);

};

export {
    http,
    headers,
};
